import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, CalendarDays, Hammer, Presentation, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { products } from '../data/products';
import { INCUBATOR_PROGRAM_FEE_LABEL } from '../lib/incubatorProgram';

const PRODUCT_PATHS: Record<string, string> = {
  'SELF Phone': '/self-phone',
  Robotics: '/robotics',
  BLOOD: '/blood',
  'Role Model AI': '/role-model-ai',
  'Cerebral OS': '/cerebral-os',
  Resonatia: '/resonatia',
  Essence: 'https://essence.selflabs.xyz',
  'Living Website': '/living-website',
  'AuRA Resonant Layer': '/aura-resonant-layer',
};

function getProductPath(title: string): string {
  return PRODUCT_PATHS[title] ?? `/${title.toLowerCase().replace(/\s+/g, '-')}`;
}

function isExternalPath(path: string): boolean {
  return /^https?:\/\//i.test(path);
}

export default function AllProducts() {
  return (
    <div className="min-h-screen bg-black text-white">
      <div
        className="pointer-events-none fixed inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 10% 80%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 90% 70%, var(--glow-blue) 0%, transparent 45%)',
        }}
      />

      <Helmet>
        <title>Products — SELF Labs</title>
        <meta
          name="description"
          content="Explore SELF Labs products — SELF Phone, Essence, Resonatia, Role Model AI, Cerebral OS, and more."
        />
      </Helmet>

      <div className="relative px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/"
              className="inline-flex items-center text-sm text-white/50 transition-colors hover:text-white group"
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
              Back to Home
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-10 text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/75">
              SELF Labs ecosystem
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight sm:text-5xl">Our Products</h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-white/55">
              AI-native hardware, avatars, operating systems, and signal technology — built by SELF Labs.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products.map((product, index) => {
              const Icon = product.icon;
              const path = getProductPath(product.title);
              const external = isExternalPath(path);
              const cardClassName =
                'group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-all hover:border-white/20 hover:bg-white/[0.05] hover:shadow-[0_0_28px_var(--glow-purple),0_0_40px_var(--glow-blue)]';
              const cardBody = (
                <>
                    <div className="mb-5 flex justify-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-white/12 bg-white/5 transition-colors group-hover:border-white/20 group-hover:bg-white/[0.08]">
                        <Icon className="h-7 w-7 text-white/75" strokeWidth={1.5} />
                      </div>
                    </div>

                    <h2 className="text-center text-xl font-semibold text-white group-hover:text-white">
                      {product.title}
                    </h2>

                    <p className="mt-3 flex-1 text-center text-sm leading-relaxed text-white/55">
                      {product.description}
                    </p>

                    {product.features && (
                      <ul className="mt-5 space-y-2 border-t border-white/8 pt-5">
                        {product.features.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-start gap-2 text-sm text-white/45 group-hover:text-white/55"
                          >
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-white/40" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}
                </>
              );
              return (
                <motion.div
                  key={product.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 + index * 0.05 }}
                  whileHover={{ y: -4 }}
                >
                  {external ? (
                    <a
                      href={path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cardClassName}
                    >
                      {cardBody}
                    </a>
                  ) : (
                    <Link to={path} className={cardClassName}>
                      {cardBody}
                    </Link>
                  )}
                </motion.div>
              );
            })}
          </div>

          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.35 }}
            className="mt-16"
            aria-labelledby="build-heading"
          >
            <div className="mb-5 flex items-center justify-center gap-3">
              <span className="h-px w-10 bg-white/20" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300/90">
                Custom software
              </span>
              <span className="h-px w-10 bg-white/20" />
            </div>

            <Link
              to="/build"
              className="group relative block overflow-hidden rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 via-white/[0.04] to-transparent p-8 transition-all hover:border-emerald-400/50 hover:bg-emerald-500/[0.08] sm:p-10"
            >
              <div
                className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full opacity-50 blur-3xl"
                style={{ background: 'rgba(52, 211, 153, 0.18)' }}
              />
              <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4 sm:items-center">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-emerald-400/35 bg-emerald-500/10">
                    <Hammer className="h-7 w-7 text-emerald-300" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-emerald-300/90">
                      Itemized quotes · Stripe deposit
                    </p>
                    <h2
                      id="build-heading"
                      className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl"
                    >
                      Build with SELF Labs
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 sm:text-base">
                      Mobile apps, web apps, AI agents, SaaS, and integrations — get a scoped quote
                      and start with a deposit.
                    </p>
                  </div>
                </div>

                <span className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-full border border-emerald-400/40 bg-emerald-500/15 px-5 py-3 text-sm font-semibold text-emerald-50 transition-transform group-hover:scale-[1.02] sm:self-center">
                  Get a quote
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.38 }}
            className="mt-10"
            aria-labelledby="consultation-heading"
          >
            <div className="mb-5 flex items-center justify-center gap-3">
              <span className="h-px w-10 bg-white/20" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-300/90">
                Founder time
              </span>
              <span className="h-px w-10 bg-white/20" />
            </div>

            <Link
              to="/consultation"
              className="group relative block overflow-hidden rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-500/10 via-white/[0.04] to-transparent p-8 transition-all hover:border-violet-400/50 hover:bg-violet-500/[0.08] sm:p-10"
            >
              <div
                className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full opacity-50 blur-3xl"
                style={{ background: 'rgba(167, 139, 250, 0.2)' }}
              />
              <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4 sm:items-center">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-violet-400/35 bg-violet-500/10">
                    <CalendarDays className="h-7 w-7 text-violet-300" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-violet-300/90">
                      Strategy sessions · $75–$300
                    </p>
                    <h2
                      id="consultation-heading"
                      className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl"
                    >
                      Book a consultation
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 sm:text-base">
                      Direct time with Milan Cheeks — AI strategy, agents, automation, robotics,
                      and product. Fixed sessions from $75.
                    </p>
                  </div>
                </div>

                <span className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-full border border-violet-400/40 bg-violet-500/15 px-5 py-3 text-sm font-semibold text-violet-50 transition-transform group-hover:scale-[1.02] sm:self-center">
                  Book now
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.4 }}
            className="mt-10"
            aria-labelledby="incubator-heading"
          >
            <div className="mb-5 flex items-center justify-center gap-3">
              <span className="h-px w-10 bg-white/20" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-300/90">
                For founders
              </span>
              <span className="h-px w-10 bg-white/20" />
            </div>

            <Link
              to="/incubator"
              className="group relative block overflow-hidden rounded-2xl border border-sky-400/30 bg-gradient-to-br from-sky-500/10 via-white/[0.04] to-transparent p-8 transition-all hover:border-sky-400/50 hover:bg-sky-500/[0.08] sm:p-10"
            >
              <div
                className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full opacity-50 blur-3xl"
                style={{ background: 'rgba(56, 189, 248, 0.2)' }}
              />
              <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4 sm:items-center">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-sky-400/35 bg-sky-500/10">
                    <Rocket className="h-7 w-7 text-sky-300" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-300/90">
                      AI Incubator · {INCUBATOR_PROGRAM_FEE_LABEL}
                    </p>
                    <h2
                      id="incubator-heading"
                      className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl"
                    >
                      SELF Labs AI Incubator
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 sm:text-base">
                      Platform build support, product credits, mentorship, and investment
                      consideration for selected companies.
                    </p>
                  </div>
                </div>

                <span className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-full border border-sky-400/40 bg-sky-500/15 px-5 py-3 text-sm font-semibold text-sky-50 transition-transform group-hover:scale-[1.02] sm:self-center">
                  View program
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.45 }}
            className="mt-10"
            aria-labelledby="investors-heading"
          >
            <div className="mb-5 flex items-center justify-center gap-3">
              <span className="h-px w-10 bg-white/20" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#c4a574]">
                For investors
              </span>
              <span className="h-px w-10 bg-white/20" />
            </div>

            <a
              href="/investors"
              className="group relative block overflow-hidden rounded-2xl border border-[#c4a574]/35 bg-gradient-to-br from-[#c4a574]/12 via-white/[0.04] to-transparent p-8 transition-all hover:border-[#c4a574]/55 hover:bg-[#c4a574]/10 sm:p-10"
            >
              <div
                className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full opacity-50 blur-3xl"
                style={{ background: 'rgba(196, 165, 116, 0.22)' }}
              />
              <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4 sm:items-center">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#c4a574]/35 bg-[#c4a574]/10">
                    <Presentation className="h-7 w-7 text-[#c4a574]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-[#c4a574]/90">
                      Investor briefing
                    </p>
                    <h2
                      id="investors-heading"
                      className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl"
                    >
                      SELF Labs investor deck
                    </h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/60 sm:text-base">
                      Company overview, live commercial numbers, product lines, and the raise —
                      $50,000,000.
                    </p>
                  </div>
                </div>

                <span className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-full border border-[#c4a574]/40 bg-[#c4a574]/15 px-5 py-3 text-sm font-semibold text-[#e8e4dc] transition-transform group-hover:scale-[1.02] sm:self-center">
                  Open deck
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </a>
          </motion.section>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-14 text-center text-sm text-white/40"
          >
            Ask Milan on the homepage to explore any product in detail.
          </motion.p>
        </div>
      </div>
    </div>
  );
}
